import { useCallback, useRef } from 'react';

export type SSEPayload = Record<string, unknown>;

export interface ConsumeSSEStreamOptions {
  url: string;
  method?: 'GET' | 'POST';
  headers?: HeadersInit;
  body?: BodyInit | null;
  signal?: AbortSignal;
  onEvent: (data: SSEPayload) => void | Promise<void>;
  onParseError?: (error: unknown, rawLine: string) => void;
}

/** Auth headers for AI backend streaming requests */
export function buildAiAuthHeaders(extra?: Record<string, string>): HeadersInit {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
}

/**
 * Read an SSE response body and invoke onEvent for each `data:` JSON payload.
 * Uses a line buffer so partial chunks are handled correctly.
 */
export async function consumeSSEStream(options: ConsumeSSEStreamOptions): Promise<void> {
  const { url, method = 'POST', headers, body, signal, onEvent, onParseError } = options;

  const response = await fetch(url, { method, headers, body, signal });

  if (!response.ok) {
    let errorMessage = `Request failed: ${response.status}`;
    try {
      const errorData = await response.json();
      errorMessage = String(errorData.message || errorData.error || errorMessage);
    } catch {
      const errorText = await response.text().catch(() => '');
      if (errorText) {
        console.error('SSE stream error:', response.status, errorText);
      }
    }
    throw new Error(errorMessage);
  }

  if (!response.body) {
    throw new Error('No response body');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';

    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed.startsWith('data:')) continue;

      const jsonStr = trimmed.replace(/^data:\s*/, '').trim();
      if (!jsonStr || jsonStr === '[DONE]') continue;

      try {
        const data = JSON.parse(jsonStr) as SSEPayload;
        if (data.type === 'error') {
          const msg = data.message ?? data.error;
          throw new Error(String(msg || 'Stream error'));
        }
        await onEvent(data);
      } catch (err) {
        if (err instanceof Error) {
          if (err.message === 'Stream error' || err.message.startsWith('Request failed')) {
            throw err;
          }
        }
        onParseError?.(err, line);
      }
    }
  }
}

export function useSSEStream() {
  const abortControllerRef = useRef<AbortController | null>(null);

  const abort = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
  }, []);

  const stream = useCallback(
    async (options: Omit<ConsumeSSEStreamOptions, 'signal'> & { signal?: AbortSignal }) => {
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      const effectiveSignal = options.signal ?? controller.signal;

      try {
        await consumeSSEStream({ ...options, signal: effectiveSignal });
      } finally {
        if (abortControllerRef.current === controller) {
          abortControllerRef.current = null;
        }
      }
    },
    [],
  );

  return {
    stream,
    abort,
    getSignal: () => abortControllerRef.current?.signal,
  };
}
